import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Search,
  FileText,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Filter,
  Calendar,
  Users,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Policies = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("policies");
  const [policies, setPolicies] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    category: "HR",
    content: "",
    version: "1.0",
    effective_date: "",
    review_date: "",
    department: "All",
    requires_acknowledgment: false,
    is_active: true,
    doc_type: "policy", // policy or procedure
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedDepartment && selectedDepartment !== "all") params.append("department", selectedDepartment);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await axios.get(`${API}/policies?${params.toString()}`);
      const allDocs = response.data.policies || [];
      setPolicies(allDocs.filter(d => d.doc_type !== "procedure"));
      setProcedures(allDocs.filter(d => d.doc_type === "procedure"));
      setCategories(response.data.categories || []);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedDepartment, searchTerm]);

  // Alias for backward compatibility
  const fetchPolicies = fetchData;

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/policies`, formData);
      toast.success("Policy created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create policy");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedPolicy) return;
    setSaving(true);
    try {
      await axios.put(`${API}/policies/${selectedPolicy.id}`, formData);
      toast.success("Policy updated successfully");
      setEditDialogOpen(false);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update policy");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Are you sure you want to archive "${policy.title}"?`)) return;
    try {
      await axios.delete(`${API}/policies/${policy.id}`);
      toast.success("Policy archived successfully");
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive policy");
    }
  };

  const handleAcknowledge = async (policy) => {
    try {
      await axios.post(`${API}/policies/${policy.id}/acknowledge`);
      toast.success("Policy acknowledged");
      fetchPolicies();
      if (selectedPolicy?.id === policy.id) {
        setSelectedPolicy({ ...selectedPolicy, user_acknowledged: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to acknowledge policy");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      category: "HR",
      content: "",
      version: "1.0",
      effective_date: "",
      review_date: "",
      department: "All",
      requires_acknowledgment: false,
      is_active: true,
      doc_type: activeTab === "procedures" ? "procedure" : "policy",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openViewDialog = async (policy) => {
    try {
      const response = await axios.get(`${API}/policies/${policy.id}`);
      setSelectedPolicy(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load details");
    }
  };

  const openEditDialog = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      title: policy.title,
      category: policy.category,
      content: policy.content,
      version: policy.version || "1.0",
      effective_date: policy.effective_date || "",
      review_date: policy.review_date || "",
      department: policy.department || "All",
      requires_acknowledgment: policy.requires_acknowledgment || false,
      is_active: policy.is_active !== false,
      doc_type: policy.doc_type || "policy",
    });
    setEditDialogOpen(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      HR: "bg-blue-500",
      Safety: "bg-red-500",
      Operations: "bg-green-500",
      IT: "bg-purple-500",
      Finance: "bg-yellow-500",
      Compliance: "bg-orange-500",
      General: "bg-gray-500",
    };
    return colors[category] || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-primary" />
            Policies & Procedures
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage company policies, procedures, and track acknowledgments
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-policy-btn">
          <Plus className="h-4 w-4 mr-2" />
          New {activeTab === "procedures" ? "Procedure" : "Policy"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Policies</p>
                <p className="text-2xl font-bold">{policies.length}</p>
              </div>
              <FileText className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Procedures</p>
                <p className="text-2xl font-bold">{procedures.length}</p>
              </div>
              <Filter className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Require Ack.</p>
                <p className="text-2xl font-bold">
                  {[...policies, ...procedures].filter((p) => p.requires_acknowledgment).length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Departments</p>
                <p className="text-2xl font-bold">{departments.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="policy-search"
              />
            </div>
            <CategoryManager
              categoryType={activeTab === "procedures" ? "procedure" : "policy"}
              label="All Categories"
              onCategoriesChange={fetchData}
              showAsDropdown={true}
              selectedValue={selectedCategory}
              onSelectionChange={setSelectedCategory}
              allowAll={true}
            />
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Policies and Procedures */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="policies" data-testid="policies-tab">
            <FileText className="h-4 w-4 mr-2" />
            Policies ({policies.length})
          </TabsTrigger>
          <TabsTrigger value="procedures" data-testid="procedures-tab">
            <BookOpen className="h-4 w-4 mr-2" />
            Procedures ({procedures.length})
          </TabsTrigger>
        </TabsList>

        {/* Policies Tab */}
        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <CardTitle>All Policies</CardTitle>
              <CardDescription>
                {policies.length} {policies.length === 1 ? "policy" : "policies"} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {policies.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No policies found</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first policy to get started
                  </p>
                  <Button onClick={openCreateDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Policy
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Ack. Required</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                        <TableCell className="font-medium">{policy.title}</TableCell>
                        <TableCell>
                          <Badge className={getCategoryColor(policy.category)}>
                            {policy.category}
                          </Badge>
                    </TableCell>
                    <TableCell>{policy.department || "All"}</TableCell>
                    <TableCell>{policy.version || "1.0"}</TableCell>
                    <TableCell>
                      {policy.effective_date
                        ? new Date(policy.effective_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {policy.requires_acknowledgment ? (
                        <Badge variant="outline" className="text-green-500 border-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openViewDialog(policy)}
                          data-testid={`view-policy-${policy.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(policy)}
                          data-testid={`edit-policy-${policy.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(policy)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-policy-${policy.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Procedures Tab */}
        <TabsContent value="procedures">
          <Card>
            <CardHeader>
              <CardTitle>All Procedures</CardTitle>
              <CardDescription>
                {procedures.length} {procedures.length === 1 ? "procedure" : "procedures"} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {procedures.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No procedures found</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first procedure to get started
                  </p>
                  <Button onClick={openCreateDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Procedure
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Ack. Required</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {procedures.map((procedure) => (
                      <TableRow key={procedure.id} data-testid={`procedure-row-${procedure.id}`}>
                        <TableCell className="font-medium">{procedure.title}</TableCell>
                        <TableCell>
                          <Badge className={getCategoryColor(procedure.category)}>
                            {procedure.category}
                          </Badge>
                        </TableCell>
                        <TableCell>{procedure.department || "All"}</TableCell>
                        <TableCell>v{procedure.version || "1.0"}</TableCell>
                        <TableCell>
                          {procedure.effective_date
                            ? new Date(procedure.effective_date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {procedure.requires_acknowledgment ? (
                            <Badge variant="default">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openViewDialog(procedure)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(procedure)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(procedure)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New {formData.doc_type === "procedure" ? "Procedure" : "Policy"}</DialogTitle>
            <DialogDescription>
              Add a new policy or procedure document
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <Select
                  value={formData.doc_type}
                  onValueChange={(v) => setFormData({ ...formData, doc_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={formData.doc_type === "procedure" ? "Procedure title" : "Policy title"}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <CategoryManager
                  categoryType={formData.doc_type === "procedure" ? "procedure" : "policy"}
                  label="Category"
                  onCategoriesChange={fetchData}
                  showAsDropdown={true}
                  selectedValue={formData.category}
                  onSelectionChange={(v) => setFormData({ ...formData, category: v })}
                  allowAll={false}
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={formData.department}
                  onValueChange={(v) => setFormData({ ...formData, department: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Departments</SelectItem>
                    <SelectItem value="HR">Human Resources</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Review Date</Label>
                <Input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={formData.department}
                  onValueChange={(v) => setFormData({ ...formData, department: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Departments</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_acknowledgment}
                    onChange={(e) =>
                      setFormData({ ...formData, requires_acknowledgment: e.target.checked })
                    }
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Requires Employee Acknowledgment</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Content * (Markdown supported)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter policy content here. Markdown formatting is supported."
                rows={12}
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Policy
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Policy</DialogTitle>
            <DialogDescription>Update policy details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Safety">Safety</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Compliance">Compliance</SelectItem>
                    <SelectItem value="General">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Review Date</Label>
                <Input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={12}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedPolicy?.title}
            </DialogTitle>
            <DialogDescription>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={getCategoryColor(selectedPolicy?.category)}>
                  {selectedPolicy?.category}
                </Badge>
                <span>Version {selectedPolicy?.version || "1.0"}</span>
                {selectedPolicy?.effective_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Effective: {new Date(selectedPolicy.effective_date).toLocaleDateString()}
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="prose prose-sm max-w-none dark:prose-invert bg-muted/50 rounded-lg p-4 whitespace-pre-wrap">
              {selectedPolicy?.content}
            </div>
            {selectedPolicy?.requires_acknowledgment && (
              <div className="border rounded-lg p-4">
                {selectedPolicy?.user_acknowledged ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span>You have acknowledged this policy</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-orange-500">
                      <AlertCircle className="h-5 w-5" />
                      <span>This policy requires your acknowledgment</span>
                    </div>
                    <Button onClick={() => handleAcknowledge(selectedPolicy)}>
                      Acknowledge
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Policies;
